import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCartStore } from '../store/useCartStore';
import { useUserStore } from '../store/useUserStore';
import { CreateCartPayload } from '@/types/prestashop';

// Query keys para React Query
const CART_KEYS = {
  active: (customerId: number) => ['cart', 'active', customerId] as const,
  detail: (cartId: number) => ['cart', 'detail', cartId] as const,
  products: (cartId: number) => ['cart', 'products', cartId] as const,
};

export const useCartWithPrestaShop = () => {
  const {
    items,
    prestaShopCartId,
    isPrestaShopCartLoading,
    prestaShopCartError,
    removeItem,
    updateQuantity,
    clearCart,
    createPrestaShopCart,
    createTemporaryCart,
    syncWithPrestaShopCart,
    loadPrestaShopCart,
    updatePrestaShopCart,
    deletePrestaShopCart,
    getPrestaShopCartId,
    ...otherCartActions
  } = useCartStore();

  const { profile } = useUserStore();
  const queryClient = useQueryClient();

  // Query para obtener productos del carrito
  const useGetCartProducts = () => {
    return useQuery({
      queryKey: CART_KEYS.products(prestaShopCartId || 0),
      queryFn: async () => {
        if (!prestaShopCartId) return [];
        const response = await fetch(`/api/es/carts/${prestaShopCartId}/products`);
        if (!response.ok) return [];
        const data = await response.json();
        return data.data;
      },
      enabled: !!prestaShopCartId,
      staleTime: 1 * 60 * 1000, // 1 minuto
      gcTime: 3 * 60 * 1000, // 3 minutos
    });
  };

  // Mutation para crear carrito
  const useCreateCart = () => {
    return useMutation({
      mutationFn: async (cartData: CreateCartPayload) => {
        const response = await fetch('/api/es/carts', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cartData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error creando carrito');
        }

        const data = await response.json();
        return data.data;
      },
      onSuccess: (newCart) => {
        if (newCart) {
          console.log('✅ Carrito creado exitosamente con ID:', newCart.id);

          // Actualizar el store con el ID del carrito creado
          useCartStore.getState().setPrestaShopCartId(newCart.id);

          if (profile?.id) {
            // Invalidar queries relacionadas
            queryClient.invalidateQueries({ queryKey: CART_KEYS.active(profile.id) });
            queryClient.setQueryData(CART_KEYS.detail(newCart.id), newCart);
          }
        }
      },
    });
  };

  // Mutation para agregar producto al carrito
  const useAddProductToCart = () => {
    return useMutation({
      mutationFn: async ({
        cartId,
        productId,
        quantity,
        productAttributeId = 0,
        addressDeliveryId = 0,
      }: {
        cartId: number;
        productId: number;
        quantity: number;
        productAttributeId?: number;
        addressDeliveryId?: number;
      }) => {
        const response = await fetch(`/api/es/carts/${cartId}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId,
            quantity,
            productAttributeId,
            addressDeliveryId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error agregando producto al carrito');
        }

        const data = await response.json();
        return data.data;
      },
      onSuccess: (updatedCart) => {
        if (updatedCart) {
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: CART_KEYS.products(updatedCart.id) });
          queryClient.invalidateQueries({ queryKey: CART_KEYS.detail(updatedCart.id) });

          if (profile?.id) {
            queryClient.invalidateQueries({ queryKey: CART_KEYS.active(profile.id) });
          }
        }
      },
    });
  };

  // Mutation para actualizar cantidad de producto
  const useUpdateProductQuantity = () => {
    return useMutation({
      mutationFn: async ({
        cartId,
        productId,
        quantity,
        productAttributeId = 0,
      }: {
        cartId: number;
        productId: number;
        quantity: number;
        productAttributeId?: number;
      }) => {
        const response = await fetch(`/api/es/carts/${cartId}/products`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId,
            quantity,
            productAttributeId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error actualizando cantidad');
        }

        const data = await response.json();
        return data.data;
      },
      onSuccess: (updatedCart) => {
        if (updatedCart) {
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: CART_KEYS.products(updatedCart.id) });
          queryClient.invalidateQueries({ queryKey: CART_KEYS.detail(updatedCart.id) });

          if (profile?.id) {
            queryClient.invalidateQueries({ queryKey: CART_KEYS.active(profile.id) });
          }
        }
      },
    });
  };

  // Mutation para eliminar producto del carrito
  const useRemoveProductFromCart = () => {
    return useMutation({
      mutationFn: async ({
        cartId,
        productId,
        productAttributeId = 0,
      }: {
        cartId: number;
        productId: number;
        productAttributeId?: number;
      }) => {
        const response = await fetch(`/api/es/carts/${cartId}/products`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId,
            productAttributeId,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error eliminando producto del carrito');
        }

        const data = await response.json();
        return data.data;
      },
      onSuccess: (updatedCart) => {
        if (updatedCart) {
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: CART_KEYS.products(updatedCart.id) });
          queryClient.invalidateQueries({ queryKey: CART_KEYS.detail(updatedCart.id) });

          if (profile?.id) {
            queryClient.invalidateQueries({ queryKey: CART_KEYS.active(profile.id) });
          }
        }
      },
    });
  };

  // Mutation para actualizar direcciones del carrito
  const useUpdateCartAddresses = () => {
    return useMutation({
      mutationFn: async ({
        cartId,
        id_customer,
        id_address_delivery,
        id_address_invoice,
        id_carrier,
      }: {
        cartId: number;
        id_customer?: number;
        id_address_delivery?: number;
        id_address_invoice?: number;
        id_carrier?: number;
      }) => {
        const response = await fetch(`/api/es/carts/${cartId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id_customer,
            id_address_delivery,
            id_address_invoice,
            id_carrier,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error actualizando carrito');
        }

        const data = await response.json();
        return data.data;
      },
      onSuccess: (updatedCart) => {
        if (updatedCart) {
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: CART_KEYS.detail(updatedCart.id) });

          if (profile?.id) {
            queryClient.invalidateQueries({ queryKey: CART_KEYS.active(profile.id) });
          }
        }
      },
    });
  };

  // Función para sincronizar carrito local con PrestaShop
  const syncCart = async () => {
    if (prestaShopCartId) {
      await syncWithPrestaShopCart();
    }
  };

  // Función para cargar carrito desde PrestaShop
  const loadFromPrestaShop = async (cartId: number) => {
    await loadPrestaShopCart(cartId);
  };

  // Función para limpiar completamente el carrito
  const clearCartCompletely = async () => {
    clearCart();
    if (prestaShopCartId) {
      await deletePrestaShopCart();
    }
  };

  // Función para obtener el ID del carrito de PrestaShop
  const getCartId = () => {
    return getPrestaShopCartId();
  };

  // Función para inicializar el carrito cuando sea necesario
  const initializeCart = async () => {
    if (!prestaShopCartId) {
      try {
        if (profile?.id) {
          // Usuario autenticado - buscar carrito existente o crear uno nuevo
          console.log('🛒 Usuario autenticado, buscando carrito activo...');
          await createPrestaShopCart();
        } else {
          // Usuario no autenticado - crear carrito temporal
          console.log('🛒 Usuario no autenticado, creando carrito temporal...');
          await createTemporaryCart();
        }
      } catch (error) {
        console.error('❌ Error inicializando carrito de PrestaShop:', error);
      }
    }
  };

  // Función para preservar el cupón en el estado (sin re-aplicar en BD)
  const preserveCoupon = (couponCode: string) => {
    console.log('🎫 Preservando cupón en el estado:', couponCode);
    // El cupón ya está en el estado del store, no necesitamos hacer nada
    // Solo logueamos para confirmar que se está preservando
    return true;
  };

  return {
    // Estado
    items,
    prestaShopCartId,
    isPrestaShopCartLoading,
    prestaShopCartError,

    // Acciones básicas
    removeItem,
    updateQuantity,
    clearCart,

    // Acciones de PrestaShop
    syncCart,
    loadFromPrestaShop,
    createPrestaShopCart,
    updatePrestaShopCart,
    deletePrestaShopCart,
    getCartId,
    initializeCart,
    preserveCoupon,

    // React Query hooks
    useGetCartProducts,
    useCreateCart,
    useAddProductToCart,
    useUpdateProductQuantity,
    useRemoveProductFromCart,
    useUpdateCartAddresses,

    // Otras acciones
    ...otherCartActions,

    // Utilidades
    isAuthenticated: !!profile?.id,
    hasPrestaShopCart: !!prestaShopCartId,
  };
};
